import { gql, NetworkStatus, useMutation, useQuery } from '@apollo/client'
import cx from 'classnames'
import { Link } from 'gatsby'
import { FaSync } from 'react-icons/fa'
import useVideoQueueLength from './useVideoQueueLength'
import { completeVideoSupervisionMutation } from './VideoPage'
import {
  CompleteVideoSupervisionMutation,
  CompleteVideoSupervisionMutationVariables,
} from './__generated__/CompleteVideoSupervisionMutation'
import {
  VideoQueueQuery,
  VideoQueueQueryVariables,
  VideoQueueQuery_videoQueue_edges_node,
} from './__generated__/VideoQueueQuery'

const videoQueueQuery = gql`
  query VideoQueueQuery($queueName: String) {
    videoQueue(queueName: $queueName) {
      edges {
        node {
          id
          kits {
            id
            sku
          }
          orderID
          queueName
          verifierEmail
        }
      }
    }
  }
`

interface VideoQueueRowProps {
  item: VideoQueueQuery_videoQueue_edges_node
}

const VideoQueueRow = ({ item }: VideoQueueRowProps) => {
  const [completeVideoSupervision, { loading: completeVideoSupervisionLoading }] = useMutation<
    CompleteVideoSupervisionMutation,
    CompleteVideoSupervisionMutationVariables
  >(completeVideoSupervisionMutation, {
    variables: { input: { id: item.id } },
  })

  return (
    <tr>
      <td>
        <Link to={`/dashboard/order/${item.orderID}`}>{item.orderID}</Link>
      </td>
      <td>
        {item.kits.map((kit) => (
          <span key={kit.id} className="mr-3">
            <Link to={`/dashboard/kit/${kit.id}`}>{kit.id}</Link>
          </span>
        ))}
      </td>
      <td>{item.verifierEmail}</td>
      <td>
        {['VideoQueue', 'VideoQueue_InProgress'].includes(item.queueName) && (
          <button
            onClick={() => completeVideoSupervision()}
            className={cx('button is-danger is-small mr-2', {
              'is-loading': completeVideoSupervisionLoading,
            })}
          >
            Mark as complete
          </button>
        )}

        <button onClick={() => alert('Not available yet')} className="button is-danger is-small">
          Join this call
        </button>
      </td>
    </tr>
  )
}

interface VideoQueueProps {
  queueName?: string
  title: string
}

const VideoQueue = ({ queueName, title }: VideoQueueProps) => {
  const {
    error,
    data,
    refetch,
    // fetchMore,
    networkStatus,
  } = useQuery<VideoQueueQuery, VideoQueueQueryVariables>(videoQueueQuery, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables: { queueName },
  })

  useVideoQueueLength()

  if (error) {
    return <p>ERROR {error.message}</p>
  }

  return (
    <>
      <nav className="level has-width-unset">
        <div className="level-left">
          <div className="level-item">
            <p className="subtitle is-5">{networkStatus === NetworkStatus.loading ? 'Loading...' : title}</p>
          </div>
        </div>

        <div className="level-right">
          <div className="level-item">
            <button
              className={cx('button is-info is-outlined', {
                'is-loading': networkStatus !== NetworkStatus.ready,
              })}
              disabled={networkStatus !== NetworkStatus.ready}
              onClick={() => refetch()}
            >
              <span className="icon is-small">
                <FaSync />
              </span>
            </button>
          </div>
        </div>
      </nav>

      <table className="table is-bordered is-fullwidth">
        <thead>
          <tr>
            <th>Order</th>
            <th>Kits</th>
            <th>Verifier</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data?.videoQueue.edges
            .filter((edge) => edge.node.queueName === (queueName || 'VideoQueue'))
            .map((item) => (
              <VideoQueueRow key={item.node.id} item={item.node} />
            ))}
        </tbody>
      </table>
    </>
  )
}

export default VideoQueue
